// Support ticket modal for creators — raise a request (appeal a decision, or
// "I don't have the original", or general) and follow the conversation.
// Backend: /api/v1/support (categories, tickets, messages).
import React, { useEffect, useState } from "react";
import { apiService } from "../services/api";

type Category = { id: string; name: string; slug: string; kind: string; description?: string };
type Ticket = {
  id: string; ticket_number?: string; subject: string; status: string;
  category_name?: string; created_at?: string; updated_at?: string;
  messages?: { id: string; content: string; author_type: string; created_at: string; admin_first_name?: string }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  // Preset for a contextual entry point (appeal / missing original).
  presetCategorySlug?: string;
  presetSubject?: string;
  presetMessage?: string;
  relatedMediaId?: string;
};

export default function SupportTicketModal({ open, onClose, presetCategorySlug, presetSubject, presetMessage, relatedMediaId }: Props) {
  const [tab, setTab] = useState<"new" | "list">("new");
  const [categories, setCategories] = useState<Category[]>([]);
  const [categorySlug, setCategorySlug] = useState(presetCategorySlug || "general");
  const [subject, setSubject] = useState(presetSubject || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [active, setActive] = useState<Ticket | null>(null);
  const [reply, setReply] = useState("");

  useEffect(() => {
    if (!open) return;
    setTab("new"); setErr(null); setOkMsg(null); setActive(null);
    setCategorySlug(presetCategorySlug || "general");
    setSubject(presetSubject || "");
    setMessage(presetMessage || "");
    setMessage("");
    apiService.getSupportCategories().then(setCategories).catch(() => {});
  }, [open, presetCategorySlug, presetSubject, presetMessage]);

  async function loadTickets() {
    try { setTickets(await apiService.getSupportTickets()); } catch { /* ignore */ }
  }
  useEffect(() => { if (open && tab === "list") loadTickets(); }, [open, tab]);

  async function submitNew() {
    if (!subject.trim() || !message.trim()) { setErr("Please add a subject and a message."); return; }
    setSubmitting(true); setErr(null);
    try {
      await apiService.createSupportTicket({ categorySlug, subject, message, relatedMediaId });
      setOkMsg("Your request has been submitted. Our team will review it.");
      setMessage("");
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Could not submit your request. Please try again.");
    } finally { setSubmitting(false); }
  }

  async function openTicket(id: string) {
    try { setActive(await apiService.getSupportTicket(id)); } catch { /* ignore */ }
  }
  async function sendReply() {
    if (!active || !reply.trim()) return;
    try { const t = await apiService.replySupportTicket(active.id, reply); setActive(t); setReply(""); } catch { /* ignore */ }
  }

  if (!open) return null;

return (
    <>
    <div 
  className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md transition-opacity" 
  style={{ height: "100dvh" }}
  aria-hidden="true" 
  onClick={onClose} 
/>
      
<div
  className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-4 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
  style={{ height: "100dvh", scrollbarWidth: "none", msOverflowStyle: "none" }}
  onClick={onClose}
>
        <div 
          className="relative w-full max-w-[600px] my-auto rounded-2xl bg-white dark:bg-[#0f0f0f] text-black dark:text-white border border-black dark:border-white shadow-2xl flex flex-col overflow-hidden" 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 bg-white dark:bg-[#0f0f0f] z-10 shrink-0">
            <h2 className="text-xl md:text-2xl font-medium font-['Product_Sans']">Support</h2>
            <button className="h-8 w-8 flex items-center justify-center rounded-full text-black dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex px-6 border-b border-gray-200 dark:border-white/10 font-['Product_Sans'] shrink-0">
            <button 
              onClick={() => setTab("new")} 
              className={`flex-1 pb-3 text-sm md:text-base font-medium transition-colors relative ${tab === "new" ? "text-black dark:text-white" : "text-gray-500 hover:text-black dark:hover:text-white"}`}
            >
              New Request
              {tab === "new" && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />}
            </button>
            <button 
              onClick={() => { setTab("list"); setActive(null); }} 
              className={`flex-1 pb-3 text-sm md:text-base font-medium transition-colors relative ${tab === "list" ? "text-black dark:text-white" : "text-gray-500 hover:text-black dark:hover:text-white"}`}
            >
              My Tickets
              {tab === "list" && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />}
            </button>
          </div>

          <div className="p-6 overflow-y-auto">
            {tab === "new" && (
              <div className="space-y-6">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-black dark:text-white font-['Product_Sans'] mb-2">Category</label>
                  <select 
                    value={categorySlug} 
                    onChange={(e) => setCategorySlug(e.target.value)}
                    className="w-full rounded-lg bg-transparent border border-gray-300 dark:border-white/20 px-4 py-3 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition appearance-none font-['Product_Sans']"
                  >
                    {categories.map((c) => (<option key={c.id} value={c.slug} className="text-black">{c.name}</option>))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-black dark:text-white font-['Product_Sans'] mb-2">Subject</label>
                  <input 
                    value={subject} 
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full rounded-lg bg-transparent border border-gray-300 dark:border-white/20 px-4 py-3 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition font-['Product_Sans']"
                    placeholder="Short summary" 
                  />
                </div>
                
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-black dark:text-white font-['Product_Sans'] mb-2">Message</label>
                  <textarea 
                    value={message} 
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full min-h-[140px] rounded-lg bg-transparent border border-gray-300 dark:border-white/20 px-4 py-3 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition resize-y font-['Product_Sans']"
                    placeholder="Tell us what happened…" 
                  />
                </div>
                
                {err && <div className="text-sm text-red-500 font-['Product_Sans']">{err}</div>}
                {okMsg && <div className="text-sm text-emerald-500 font-['Product_Sans']">{okMsg}</div>}
                
                <div className="pt-2 flex justify-end">
                  <button 
                    onClick={submitNew} 
                    disabled={submitting}
                    className="px-6 py-3.5 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 font-['Product_Sans']"
                  >
                    {submitting ? "Submitting…" : "Submit Request"}
                  </button>
                </div>
              </div>
            )}

            {tab === "list" && !active && (
              <div className="space-y-3">
                {tickets.length === 0 && <div className="text-sm text-gray-500 font-['Product_Sans'] text-center py-8">No tickets yet.</div>}
                {tickets.map((t) => (
                  <button key={t.id} onClick={() => openTicket(t.id)}
                    className="w-full text-left rounded-lg border border-gray-300 dark:border-white/20 bg-transparent p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <div className="text-sm font-medium truncate font-['Product_Sans'] text-black dark:text-white">{t.subject}</div>
                      <span className="text-[10px] uppercase tracking-wider font-bold rounded-full border border-gray-300 dark:border-white/20 px-2.5 py-0.5 text-gray-600 dark:text-gray-300 shrink-0 whitespace-nowrap">
                        {t.status?.replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 font-['Product_Sans'] truncate">{t.category_name} · {t.ticket_number}</div>
                  </button>
                ))}
              </div>
            )}

            {tab === "list" && active && (
              <div className="space-y-5">
                <button onClick={() => setActive(null)} className="text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-black dark:hover:text-white transition font-['Product_Sans']">← All tickets</button>
                
                <div>
                  <div className="text-lg font-semibold font-['Product_Sans'] text-black dark:text-white">{active.subject}</div>
                  <div className="text-xs text-gray-500 font-['Product_Sans'] mt-1">{active.category_name} · {active.ticket_number} · <span className="uppercase font-medium">{active.status?.replace("_", " ")}</span></div>
                </div>
                
                <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                  {(active.messages || []).map((m) => (
                    <div key={m.id} className={`rounded-xl p-4 text-sm font-['Product_Sans'] ${m.author_type === "admin" ? "bg-gray-100 dark:bg-white/10" : "bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10"}`}>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">
                        {m.author_type === "admin" ? (m.admin_first_name ? `Vastavik · ${m.admin_first_name}` : "Vastavik team") : "You"}
                      </div>
                      <div className="text-black dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{m.content}</div>
                    </div>
                  ))}
                </div>
                
                {active.status !== "closed" && (
                  <div className="flex gap-3 pt-2">
                    <input 
                      value={reply} 
                      onChange={(e) => setReply(e.target.value)}
                      className="flex-1 rounded-lg bg-transparent border border-gray-300 dark:border-white/20 px-4 py-3 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition font-['Product_Sans']" 
                      placeholder="Reply…" 
                    />
                    <button 
                      onClick={sendReply} 
                      disabled={!reply.trim()}
                      className="rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50 transition font-['Product_Sans']"
                    >
                      Send
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
